import {
  CAM_IN_CAM_SIZES,
  FEED_RATE_UNITS,
  GAMEPAD_DIRECTIONS,
  GAMEPAD_MODES,
  KEYBOARD_PRESETS,
  RENDER_DETECTION_MODES,
} from "@observe-tech/utils";
import {
  fail,
  oneOf,
  bool,
  stringList,
  IPV4,
  KEY_CODE,
} from "../infra/validation";
import type {
  AutoUpdateSettings,
  DatarouterConfig,
  FeedSettings,
  GamepadSettings,
  KeyboardSettings,
  VideoSettings,
} from "../types";

export const assertValidDatarouter = (config: DatarouterConfig): void => {
  bool(config.enabled, "datarouter.enabled");
  bool(config.connectsToCloud, "datarouter.connectsToCloud");
  const match = typeof config.ip === "string" ? IPV4.exec(config.ip) : null;
  if (!match || config.ip.length > 15)
    return fail(`datarouter.ip is not an ipv4 address: ${config.ip}`);
  if (match.slice(1).some((octet) => Number(octet) > 255))
    fail(`datarouter.ip octet out of range: ${config.ip}`);
  const port = Number(config.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535)
    fail(`datarouter.port out of range: ${config.port}`);
};

export const assertValidIpJson = (value: unknown): void => {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    fail("ip.json content must be a json object");
};

export const assertValidAutoUpdateSettings = (
  settings: AutoUpdateSettings,
): void => {
  bool(settings.enabled, "autoUpdateSettings.enabled");
};

export const assertValidFeedSettings = (settings: FeedSettings): void => {
  oneOf(settings.feedRateUnit, FEED_RATE_UNITS, "feedSettings.feedRateUnit");
  if (!(settings.manualFeedingIncrement > 0))
    fail("feedSettings.manualFeedingIncrement must be > 0");
  if (!(settings.manualAppetiteIncrement > 0))
    fail("feedSettings.manualAppetiteIncrement must be > 0");
};

export const assertValidVideoSettings = (settings: VideoSettings): void => {
  const { defaults, infoRowOverrides } = settings;
  stringList(
    defaults.defaultInfoRows,
    "videoSettings.defaults.defaultInfoRows",
  );
  if (!(defaults.defaultTimeRangeSeconds > 0))
    fail("videoSettings.defaults.defaultTimeRangeSeconds must be > 0");
  oneOf(
    defaults.camInCamSize,
    CAM_IN_CAM_SIZES,
    "videoSettings.defaults.camInCamSize",
  );
  oneOf(
    defaults.defaultRenderDetections,
    RENDER_DETECTION_MODES,
    "videoSettings.defaults.defaultRenderDetections",
  );
  for (const [key, rows] of Object.entries(infoRowOverrides))
    stringList(rows, `videoSettings.infoRowOverrides[${key}]`);
};

export const assertValidGamepadSettings = (settings: GamepadSettings): void => {
  bool(settings.enabled, "gamepadSettings.enabled");
  if (settings.bindings === null) return;
  for (const button of settings.bindings.buttons) {
    if (
      !Number.isInteger(button.buttonIndex) ||
      button.buttonIndex < -1 ||
      button.buttonIndex > 17
    )
      fail(`gamepad buttonIndex out of range: ${button.buttonIndex}`);
    if (
      typeof button.commandName !== "string" ||
      button.commandName.length === 0
    )
      fail("gamepad button commandName is required");
    oneOf(button.mode, GAMEPAD_MODES, "gamepad button mode");
  }
  for (const axis of settings.bindings.axes) {
    if (
      !Number.isInteger(axis.axisIndex) ||
      axis.axisIndex < 0 ||
      axis.axisIndex > 3
    )
      fail(`gamepad axisIndex out of range: ${axis.axisIndex}`);
    oneOf(axis.direction, GAMEPAD_DIRECTIONS, "gamepad axis direction");
    if (typeof axis.commandName !== "string" || axis.commandName.length === 0)
      fail("gamepad axis commandName is required");
    if (!(axis.deadzone >= 0 && axis.deadzone < 1))
      fail(`gamepad deadzone out of [0,1): ${axis.deadzone}`);
  }
};

export const assertValidKeyboardSettings = (
  settings: KeyboardSettings,
): void => {
  oneOf(settings.preset, KEYBOARD_PRESETS, "keyboardSettings.preset");
  if (settings.customBindings === null) return;
  for (const binding of settings.customBindings) {
    if (
      typeof binding.commandName !== "string" ||
      binding.commandName.length === 0
    )
      fail("keyboard binding commandName is required");
    for (const code of binding.binding)
      if (!KEY_CODE.test(code))
        fail(`keyboard binding code is not a key code: ${code}`);
  }
};
